import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PackageOpen, Plus, Trash2 } from "lucide-react";
import { Button, IconButton, Input } from "@akuna/ui";
import { useToast } from "@/hooks/use-toast";
import {
  useCreatePetStoredItem,
  useKeepPetStoredItem,
  useUnkeepPetStoredItem,
  useUpdatePetStoredItem,
} from "../usePetProfileMutations";
import { usePetProfileUndo } from "../usePetProfileUndo";
import type { StoredPetItem } from "../usePetReservationSummary";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

export type PetStoredItemsSectionProps = {
  petId: string;
  storedItems: StoredPetItem[];
  compact?: boolean;
  showEmpty?: boolean;
};

export function PetStoredItemsSection({ petId, storedItems, compact, showEmpty }: PetStoredItemsSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const createItem = useCreatePetStoredItem();
  const updateItem = useUpdatePetStoredItem();
  const unkeepItem = useUnkeepPetStoredItem();
  const keepItem = useKeepPetStoredItem();
  const { removeWithUndo } = usePetProfileUndo();
  const [isEditing, setIsEditing] = useState(false);

  if (compact && !showEmpty && !isEditing && storedItems.length === 0) return null;

  const fail = () => toast({ title: t("petProfile.saveError"), variant: "destructive" });

  const persist = async (item: StoredPetItem, patch: Partial<Pick<StoredPetItem, "itemName" | "storageLocation">>) => {
    try {
      await updateItem.mutateAsync({
        id: item.id,
        petId,
        itemName: patch.itemName ?? item.itemName,
        storageLocation: patch.storageLocation !== undefined ? patch.storageLocation : item.storageLocation,
      });
    } catch {
      fail();
    }
  };

  const add = async () => {
    try {
      await createItem.mutateAsync({ petId, itemName: "", storageLocation: null });
    } catch {
      fail();
    }
  };

  const remove = (item: StoredPetItem) =>
    removeWithUndo({
      message: t("petProfile.storedItemRemoved"),
      remove: () => unkeepItem.mutateAsync({ id: item.id, petId }),
      restore: () => keepItem.mutateAsync({ id: item.id, petId }),
    });

  return (
    <PetProfileSectionShell
      title={t("petProfile.storedItems")}
      icon={PackageOpen}
      tone="purple"
      count={storedItems.length}
      isEditing={isEditing}
      onToggleEdit={() => setIsEditing((current) => !current)}
      editLabel={t("petProfile.editStoredItems")}
      doneLabel={t("petProfile.doneStoredItems")}
      compact={compact && !isEditing}
    >
      {isEditing ? storedItems.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.storedItemsEmpty")}</PetProfileSectionEmpty>
      ) : (
        <ul className="grid gap-2 mt-2">
          {storedItems.map((item) => (
            <li key={item.id} className="flex  items-start gap-2 rounded-md border border-border bg-muted/30 p-2">
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
                <div className="grid min-w-0 gap-1">
                  <label className="text-xs font-medium text-muted-foreground">{t("petProfile.storedItemName")}</label>
                  <Input
                    aria-label={t("petProfile.storedItemName")}
                    className="bg-card"
                    defaultValue={item.itemName}
                    placeholder={t("petProfile.storedItemNamePlaceholder")}
                    onBlur={(event) => {
                      const itemName = event.target.value;
                      if (itemName === item.itemName) return;
                      void persist(item, { itemName });
                    }}
                  />
                </div>
                <div className="grid min-w-0 gap-1">
                  <label className="text-xs font-medium text-muted-foreground">{t("petProfile.storedItemLocation")}</label>
                  <Input
                    aria-label={t("petProfile.storedItemLocation")}
                    className="h-8 min-w-0 flex-1 bg-card text-sm"
                    defaultValue={item.storageLocation ?? ""}
                    placeholder={t("petProfile.storedItemLocationPlaceholder")}
                    onBlur={(event) => {
                      const storageLocation = event.target.value;
                      if (storageLocation === (item.storageLocation ?? "")) return;
                      void persist(item, { storageLocation: storageLocation || null });
                    }}
                  />
                </div>
              </div>
              <IconButton
                variant="ghost"
                size="sm"
                className="shrink-0 text-muted-foreground hover:text-state-danger"
                label={t("petProfile.removeStoredItem")}
                onClick={() => void remove(item)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : storedItems.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.storedItemsEmpty")}</PetProfileSectionEmpty>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {storedItems.map((item) => (
            <span key={item.id} className="max-w-full rounded-full border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
              {item.itemName || "—"}
              {item.storageLocation ? <><span className="mx-1 text-purple-400">·</span>{item.storageLocation}</> : null}
            </span>
          ))}
        </div>
      )}

      {isEditing ? <Button variant="outline" size="sm" className="w-full border-dashed mt-2" onClick={() => void add()}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t("petProfile.addStoredItem")}
      </Button> : null}
    </PetProfileSectionShell>
  );
}
